/* eslint-disable sort-imports */
/**
 * Labor standards — the data: the catalog and the master data its form
 * needs (operation types, products, work centers, jobs, work phases), plus
 * the two writes (save, delete) and the page's one notice.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import {
	LaborStandard,
	LaborStandardOperationInput,
	laborStandardsApi,
	OperationType,
} from "../../../../services/labor-standards-api";
import { Product, productsApi } from "../../../../services/products-api";
import { WorkPhase, workPhasesApi } from "../../../../services/work-phases-api";
import axios from "../../../../utils/api-client";
import { apiErrorMessage } from "../../../../utils/api-error";
import { StoreType } from "../../../../utils/types";
import { operationWorkstations } from "../../utils/standard-matching";
import { MasterJob } from "../ui/phase-row";
import { getRoleBreakdown } from "../utils/standard-helpers";

interface MasterWorkcenter {
	id: number;
	name: string;
}

export type SnackbarSeverity = "success" | "error";

export interface SnackbarState {
	open: boolean;
	message: string;
	severity?: SnackbarSeverity;
}

/** What the API takes for a create or update, built from the form. */
export type StandardSavePayload = Omit<LaborStandard, "id" | "createdDate" | "modifiedDate" | "operations"> & {
	operations: LaborStandardOperationInput[];
};

/** The catalog, filtered by the search box (product, SKU, recipe group, work center). */
export const filterStandards = (standards: LaborStandard[], query: string, productById: Map<number, Product>): LaborStandard[] => {
	if (!query.trim()) return standards;
	const needle = query.toLowerCase();
	return standards.filter((standard) => {
		const product = standard.productId ? productById.get(standard.productId) : undefined;
		const workCenters = [
			standard.workstation || "",
			...(standard.workstations || []),
			...(standard.operations || []).flatMap(operationWorkstations),
		];
		return (
			(standard.productName || "").toLowerCase().includes(needle) ||
			(standard.sku || "").toLowerCase().includes(needle) ||
			(standard.productItemNumber || "").toLowerCase().includes(needle) ||
			(standard.recipeGroup || "").toLowerCase().includes(needle) ||
			workCenters.some((name) => name.toLowerCase().includes(needle)) ||
			(!!product &&
				(product.itemNumber.toLowerCase().includes(needle) ||
					product.name.toLowerCase().includes(needle) ||
					(!!product.description && product.description.toLowerCase().includes(needle))))
		);
	});
};

export function useLaborStandards() {
	const userId = useSelector((state: StoreType) => state.user.userData.id);
	const companyId = useSelector((state: StoreType) => state.user.userData.companyId);

	// Data states
	const [laborStandards, setLaborStandards] = useState<LaborStandard[]>([]);
	const [operationTypes, setOperationTypes] = useState<OperationType[]>([]);
	const [products, setProducts] = useState<Product[]>([]);
	const [workcenters, setWorkcenters] = useState<string[]>([]);
	const [jobRoles, setJobRoles] = useState<MasterJob[]>([]);
	// The company's Work Phases vocabulary — the "Phase type" of a labor phase.
	const [workPhases, setWorkPhases] = useState<WorkPhase[]>([]);

	// Loading states
	const [loading, setLoading] = useState(false);
	const [loadingProducts, setLoadingProducts] = useState(false);
	const [loadingMasterData, setLoadingMasterData] = useState(false);

	const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: "" });
	const notify = useCallback((message: string, severity: SnackbarSeverity) => {
		setSnackbar({ open: true, message, severity });
	}, []);
	const closeSnackbar = useCallback(() => setSnackbar((prev) => ({ ...prev, open: false })), []);

	// ==================== Data Loading ====================

	// The company's work phases, for the Phase type picker. Only active ones
	// are offered; an inactive one already on a phase is still shown by id.
	useEffect(() => {
		if (!companyId) return;
		let cancelled = false;
		workPhasesApi
			.getByCompany(companyId)
			.then((data) => {
				if (cancelled) return;
				setWorkPhases([...data].sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code)));
			})
			.catch((error) => {
				console.error("Failed to fetch work phases:", error);
			});
		return () => {
			cancelled = true;
		};
	}, [companyId]);

	// Fetch master data (workcenters and jobs)
	useEffect(() => {
		const fetchMasterData = async () => {
			setLoadingMasterData(true);
			const errors: string[] = [];

			try {
				const workcentersRes = await axios.get<MasterWorkcenter[]>(`${process.env.REACT_APP_API_END_POINT}/WorkCenters/GetWorkcentersByUser`);
				setWorkcenters(workcentersRes.data?.map((wc) => wc.name).sort() || []);
			} catch (error) {
				console.error("Failed to fetch workcenters:", error);
				errors.push("workcenters");
			}

			try {
				const jobsRes = await axios.get<MasterJob[]>(`${process.env.REACT_APP_API_END_POINT}/Jobs/GetJobsByUser`, {
					params: { userid: userId },
				});
				// Sort by name and store full objects (id + name)
				setJobRoles(jobsRes.data?.sort((a, b) => a.name.localeCompare(b.name)) || []);
			} catch (error) {
				console.error("Failed to fetch jobs:", error);
				errors.push("job roles");
			}

			if (errors.length > 0) {
				notify(`Failed to load ${errors.join(" and ")}`, "error");
			}
			setLoadingMasterData(false);
		};
		fetchMasterData();
	}, [userId, notify]);

	// Fetch products
	useEffect(() => {
		const fetchProducts = async () => {
			try {
				setLoadingProducts(true);
				const data = await productsApi.getAll(false);
				setProducts(data);
			} catch (error) {
				console.error("Failed to fetch products:", error);
				notify("Failed to load products", "error");
			} finally {
				setLoadingProducts(false);
			}
		};
		fetchProducts();
	}, [notify]);

	// Fetch operation types and labor standards
	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const [typesData, standardsData] = await Promise.all([laborStandardsApi.getOperationTypes(), laborStandardsApi.getAll()]);
				setOperationTypes(typesData);
				setLaborStandards(standardsData);
			} catch (error) {
				console.error("Failed to fetch labor standards data:", error);
				notify("Failed to load labor standards", "error");
			} finally {
				setLoading(false);
			}
		};
		fetchData();
	}, [notify]);

	/** After an upload: the catalog again, from the server. */
	const reloadStandards = useCallback(() => {
		laborStandardsApi.getAll().then(setLaborStandards).catch(console.error);
	}, []);

	// ==================== Derived ====================

	const productById = useMemo(() => {
		const map = new Map<number, Product>();
		products.forEach((product) => map.set(product.id, product));
		return map;
	}, [products]);

	// Role-minutes aggregation is O(operations x phases) per standard; compute it
	// once per data change instead of per card per render.
	const roleBreakdownByStandard = useMemo(() => {
		const map = new Map<number, [string, number][]>();
		laborStandards.forEach((standard) => {
			if (standard.id != null) map.set(standard.id, getRoleBreakdown(standard));
		});
		return map;
	}, [laborStandards]);

	const standardsForProduct = useCallback(
		(productId: number) => laborStandards.filter((ls) => ls.productId === productId),
		[laborStandards]
	);

	// ==================== Writes ====================

	/** Create or update; the catalog is patched in place. Null when the save failed. */
	const saveStandard = useCallback(
		async (payload: StandardSavePayload, editingId: number | null): Promise<LaborStandard | null> => {
			try {
				if (editingId != null) {
					const saved = await laborStandardsApi.update(editingId, { ...payload, id: editingId });
					setLaborStandards((prev) => prev.map((ls) => (ls.id === editingId ? saved : ls)));
					notify("Labor standard updated successfully", "success");
					return saved;
				}
				const saved = await laborStandardsApi.create(payload);
				setLaborStandards((prev) => [...prev, saved]);
				notify("Labor standard created successfully", "success");
				return saved;
			} catch (error) {
				console.error("Failed to save:", error);
				notify(apiErrorMessage(error, "Failed to save labor standard"), "error");
				return null;
			}
		},
		[notify]
	);

	const deleteStandard = useCallback(
		async (id: number) => {
			if (!window.confirm("Are you sure you want to delete this labor standard?")) return;

			try {
				await laborStandardsApi.delete(id, true);
				setLaborStandards((prev) => prev.filter((ls) => ls.id !== id));
				notify("Labor standard deleted successfully", "success");
			} catch (error) {
				console.error("Failed to delete:", error);
				notify("Failed to delete labor standard", "error");
			}
		},
		[notify]
	);

	return {
		laborStandards,
		operationTypes,
		products,
		workcenters,
		jobRoles,
		workPhases,
		loading,
		loadingProducts,
		isLoadingAnything: loading || loadingProducts || loadingMasterData,
		productById,
		roleBreakdownByStandard,
		standardsForProduct,
		snackbar,
		notify,
		closeSnackbar,
		reloadStandards,
		saveStandard,
		deleteStandard,
	};
}

export type LaborStandardsData = ReturnType<typeof useLaborStandards>;
